import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useOutsideClick,
} from '@chakra-ui/react';
import {
  LuArrowLeft,
  LuBuilding2,
  LuCheck,
  LuChevronDown,
  LuChevronUp,
  LuHash,
  LuMapPin,
  LuSearch,
  LuSparkles,
  LuStar,
} from 'react-icons/lu';
import {
  parseCityFromAddress,
  getQualityLabel,
} from '../utils/marketKeywordResolver';
import {
  resolveMarketSearch,
  searchBusinesses,
  validateMarketKeyword,
} from '../services/marketSearchService';

const SEARCH_DEBOUNCE_MS = 450;

function qualityColor(label) {
  switch (label) {
    case 'Excellent':
      return 'green';
    case 'Good':
      return 'blue';
    case 'Medium':
      return 'orange';
    default:
      return 'gray';
  }
}

function BusinessResultCard({ business, isSelected, onSelect, subtleText, headingColor, inputBg, subtleBorder }) {
  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      p={3}
      bg={isSelected ? 'blue.50' : inputBg}
      borderRadius="10px"
      borderWidth="1px"
      borderColor={isSelected ? 'blue.300' : subtleBorder}
      _hover={{ borderColor: 'blue.200' }}
      transition="border-color 0.15s"
      onClick={() => onSelect(business)}
    >
      <HStack justify="space-between" align="flex-start" spacing={3}>
        <Box flex={1} minW={0}>
          <HStack spacing={2} align="center">
            <Flex
              w="26px"
              h="26px"
              borderRadius="6px"
              bg="blue.50"
              justify="center"
              align="center"
              flexShrink={0}
            >
              <Icon as={LuBuilding2} boxSize="12px" color="blue.500" />
            </Flex>
            <Text fontSize="sm" fontWeight="600" color={headingColor} noOfLines={1}>
              {business.name}
            </Text>
          </HStack>
          {(business.primary_type_display || business.address) && (
            <Text fontSize="xs" color={subtleText} mt={1} ml="34px" noOfLines={2}>
              {[business.primary_type_display, business.address].filter(Boolean).join(' · ')}
            </Text>
          )}
        </Box>
        <VStack spacing={0.5} align="flex-end" flexShrink={0}>
          {business.rating != null && (
            <HStack spacing={1}>
              <Icon as={LuStar} boxSize="11px" color="orange.400" />
              <Text fontSize="xs" fontWeight="600" color={headingColor}>
                {business.rating.toFixed(1)}
              </Text>
            </HStack>
          )}
          {business.review_count > 0 && (
            <Text fontSize="10px" color={subtleText}>
              {business.review_count.toLocaleString()} reviews
            </Text>
          )}
        </VStack>
      </HStack>
    </Box>
  );
}

/**
 * Guides users from GMB selection to a validated market search keyword.
 */
function MarketSearchWizard({ onComplete, onCancel }) {
  const [wizardStep, setWizardStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [anchorBusiness, setAnchorBusiness] = useState(null);
  const [isResolving, setIsResolving] = useState(false);
  const [resolution, setResolution] = useState(null);
  const [resolutionError, setResolutionError] = useState('');
  const [editedKeyword, setEditedKeyword] = useState('');
  const [editedCity, setEditedCity] = useState('');
  const [editedNeighborhood, setEditedNeighborhood] = useState('');
  const [showAlternates, setShowAlternates] = useState(false);
  const [selectedAlternateQuery, setSelectedAlternateQuery] = useState('');
  const [isValidatingSelection, setIsValidatingSelection] = useState(false);

  const searchWrapperRef = useRef(null);
  const debounceRef = useRef(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const subtleBorder = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.800', 'white');
  const subtleText = useColorModeValue('gray.500', 'gray.400');

  useOutsideClick({
    ref: searchWrapperRef,
    handler: () => {
      if (!anchorBusiness) {
        setSearchResults([]);
      }
    },
  });

  const parsedAnchorMarket = useMemo(
    () => parseCityFromAddress(anchorBusiness?.address || ''),
    [anchorBusiness],
  );

  const activeRecommendation = useMemo(() => {
    if (selectedAlternateQuery && resolution?.alternates?.length) {
      return (
        resolution.alternates.find((item) => item.query === selectedAlternateQuery) ||
        resolution.recommended
      );
    }
    return resolution?.recommended || null;
  }, [resolution, selectedAlternateQuery]);

  const displayKeyword = editedKeyword.trim() || activeRecommendation?.query || '';
  const displayMarket = editedCity.trim() || resolution?.marketLabel || parsedAnchorMarket.marketLabel;

  const runBusinessSearch = useCallback(async (query) => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError('');
    try {
      const results = await searchBusinesses(trimmed, 8);
      setSearchResults(results);
    } catch (err) {
      setSearchResults([]);
      setSearchError(err?.response?.data?.detail || err.message || 'Could not search Google Maps.');
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (wizardStep !== 1) return undefined;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      runBusinessSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, wizardStep, runBusinessSearch]);

  const runResolver = useCallback(async (business, overrides = {}) => {
    setIsResolving(true);
    setResolutionError('');
    setSelectedAlternateQuery('');

    try {
      const result = await resolveMarketSearch(business, {
        userKeyword: overrides.userKeyword || '',
        neighborhood: overrides.neighborhood || '',
        manualCity: overrides.manualCity || '',
        maxValidations: 12,
      });

      setResolution(result);
      setEditedKeyword(result.recommended?.query || '');
      setEditedCity(result.marketLabel || parseCityFromAddress(business.address).marketLabel);
      setEditedNeighborhood(overrides.neighborhood || '');
      if (!result.targetResolved) {
        setResolutionError(
          'We could not find this business in the top 20 for any local search we tried. Try a different GMB or adjust the market.',
        );
      }
      setWizardStep(2);
    } catch (err) {
      setResolutionError(err?.response?.data?.detail || err.message || 'Could not resolve a market keyword.');
    } finally {
      setIsResolving(false);
    }
  }, []);

  const handleSelectBusiness = async (business) => {
    setAnchorBusiness(business);
    setSearchQuery(business.name);
    setSearchResults([]);
    await runResolver(business);
  };

  const handleReResolve = async () => {
    if (!anchorBusiness) return;
    await runResolver(anchorBusiness, {
      userKeyword: editedKeyword,
      neighborhood: editedNeighborhood,
      manualCity: editedCity,
    });
  };

  const handleUseRecommended = async () => {
    if (!anchorBusiness || !displayKeyword) return;

    if (!activeRecommendation?.targetFound) {
      setResolutionError(
        'The prospect must appear in the top 20. Re-test keyword or select a different GMB.',
      );
      return;
    }

    setIsValidatingSelection(true);
    try {
      const competitors = await validateMarketKeyword(displayKeyword, 20);
      const targetIndex = competitors.findIndex(
        (competitor) =>
          (competitor.place_id && competitor.place_id === anchorBusiness.place_id) ||
          (competitor.name || '').trim().toLowerCase() ===
            (anchorBusiness.name || '').trim().toLowerCase(),
      );
      const targetFound = targetIndex >= 0;
      const recommendation = {
        ...(activeRecommendation || {}),
        query: displayKeyword,
        targetFound,
        targetPosition: targetFound ? targetIndex + 1 : null,
        competitorCount: competitors.length,
        qualityLabel: getQualityLabel(activeRecommendation?.score || 0),
      };

      onComplete({
        anchorBusiness,
        marketKeyword: displayKeyword,
        marketLabel: displayMarket,
        niche:
          anchorBusiness.primary_type_display ||
          resolution?.categoryLabel ||
          '',
        region: displayMarket,
        recommendation,
        competitors,
      });
    } catch (err) {
      setResolutionError(err?.response?.data?.detail || err.message || 'Could not validate the selected keyword.');
    } finally {
      setIsValidatingSelection(false);
    }
  };

  if (wizardStep === 1) {
    return (
      <VStack spacing={4} align="stretch">
        <Box>
          <Text fontSize="sm" fontWeight="600" color={headingColor} mb={1}>
            Choose the business
          </Text>
          <Text fontSize="xs" color={subtleText}>
            Search Google Business Profiles. We&apos;ll recommend the strongest local market search.
          </Text>
        </Box>

        <Box ref={searchWrapperRef} position="relative">
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="44px">
              <Icon as={LuSearch} color="gray.400" boxSize="16px" />
            </InputLeftElement>
            <Input
              placeholder='Search e.g. "Junk King Nashville"'
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setAnchorBusiness(null);
                setResolution(null);
              }}
              bg={inputBg}
              borderColor={subtleBorder}
              borderRadius="10px"
              h="44px"
              fontSize="sm"
              _focus={{ borderColor: 'blue.400', bg: cardBg }}
              _placeholder={{ color: 'gray.400' }}
            />
          </InputGroup>

          {(isSearching || searchResults.length > 0 || searchError) && (
            <Box
              position="absolute"
              top="calc(100% + 8px)"
              left={0}
              right={0}
              bg={cardBg}
              borderWidth="1px"
              borderColor={subtleBorder}
              borderRadius="12px"
              shadow="lg"
              zIndex={20}
              overflow="hidden"
              maxH="320px"
              overflowY="auto"
            >
              {isSearching ? (
                <Flex justify="center" align="center" py={6}>
                  <Spinner size="sm" color="blue.500" />
                </Flex>
              ) : searchError ? (
                <Text px={4} py={3} fontSize="sm" color="red.500">
                  {searchError}
                </Text>
              ) : (
                <VStack spacing={0} align="stretch">
                  {searchResults.map((business) => (
                    <Box key={business.place_id || business.name} px={2} py={2}>
                      <BusinessResultCard
                        business={business}
                        isSelected={anchorBusiness?.place_id === business.place_id}
                        onSelect={handleSelectBusiness}
                        subtleText={subtleText}
                        headingColor={headingColor}
                        inputBg={inputBg}
                        subtleBorder={subtleBorder}
                      />
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          )}
        </Box>

        {isResolving && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={8}
            px={4}
            bg={inputBg}
            borderRadius="12px"
            borderWidth="1px"
            borderColor={subtleBorder}
            gap={3}
          >
            <Spinner size="md" color="blue.500" thickness="3px" />
                    <Text fontSize="sm" fontWeight="600" color={headingColor} mt={0.5}>
                      Finding the best market search...
                    </Text>
                    <Text fontSize="xs" color={subtleText} textAlign="center">
                      Trying city-wide and neighborhood searches until the prospect appears
                    </Text>
          </Flex>
        )}

        {resolutionError && (
          <Text fontSize="sm" color="red.500">
            {resolutionError}
          </Text>
        )}

        <HStack spacing={3}>
          <Button variant="ghost" flex={1} onClick={onCancel} borderRadius="10px">
            Cancel
          </Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Box
        p={4}
        bg="blue.50"
        borderRadius="12px"
        borderWidth="1px"
        borderColor="blue.200"
      >
        <HStack spacing={2} mb={2}>
          <Icon as={LuBuilding2} boxSize="13px" color="blue.500" />
          <Text fontSize="10px" fontWeight="700" color="blue.600" textTransform="uppercase" letterSpacing="0.07em">
            We found
          </Text>
        </HStack>
        <Text fontSize="sm" fontWeight="700" color="blue.900">
          {anchorBusiness?.name}
        </Text>
        <Text fontSize="xs" color="blue.700" mt={1}>
          {[anchorBusiness?.primary_type_display, displayMarket].filter(Boolean).join(' · ')}
        </Text>
      </Box>

      <Box>
        <HStack spacing={2} mb={2}>
          <Icon as={LuSparkles} boxSize="14px" color="blue.500" />
          <Text fontSize="sm" fontWeight="600" color={headingColor}>
            Confirm market
          </Text>
        </HStack>
        <Text fontSize="xs" color={subtleText} mb={3}>
          This keyword tells the backend what Google search to use for competitors.
        </Text>

        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
              Recommended market search
            </Text>
            <Input
              value={editedKeyword}
              onChange={(e) => {
                setEditedKeyword(e.target.value);
                setSelectedAlternateQuery('');
              }}
              bg={inputBg}
              borderColor={subtleBorder}
              borderRadius="10px"
              h="44px"
              fontSize="sm"
              _focus={{ borderColor: 'blue.400', bg: cardBg }}
            />
          </Box>

          <SimpleGridLike spacing={4}>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                City / market
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none" h="44px">
                  <Icon as={LuMapPin} color="gray.400" boxSize="16px" />
                </InputLeftElement>
                <Input
                  value={editedCity}
                  onChange={(e) => setEditedCity(e.target.value)}
                  bg={inputBg}
                  borderColor={subtleBorder}
                  borderRadius="10px"
                  h="44px"
                  fontSize="sm"
                  pl="40px"
                  _focus={{ borderColor: 'blue.400', bg: cardBg }}
                />
              </InputGroup>
            </Box>
            <Box flex={1}>
              <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                Neighborhood
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none" h="44px">
                  <Icon as={LuHash} color="gray.400" boxSize="16px" />
                </InputLeftElement>
                <Input
                  placeholder="Optional"
                  value={editedNeighborhood}
                  onChange={(e) => setEditedNeighborhood(e.target.value)}
                  bg={inputBg}
                  borderColor={subtleBorder}
                  borderRadius="10px"
                  h="44px"
                  fontSize="sm"
                  pl="40px"
                  _focus={{ borderColor: 'blue.400', bg: cardBg }}
                />
              </InputGroup>
            </Box>
          </SimpleGridLike>
        </VStack>
      </Box>

      {activeRecommendation && (
        <Box
          p={4}
          bg={inputBg}
          borderRadius="12px"
          borderWidth="1px"
          borderColor={subtleBorder}
        >
          <Text fontSize="xs" fontWeight="700" color={subtleText} textTransform="uppercase" letterSpacing="0.06em" mb={2}>
            Match quality
          </Text>
          <SimpleGridLike spacing={4}>
            <Metric label="Score" value={`${activeRecommendation.score}/100`} />
            <Metric
              label="Prospect in top 20"
              value={
                activeRecommendation.targetFound
                  ? `Yes, #${activeRecommendation.targetPosition}`
                  : 'Still searching…'
              }
            />
            <Metric label="Competitors found" value={String(activeRecommendation.competitorCount || 0)} />
            <Metric label="Category match" value={`${activeRecommendation.categoryMatchPct || 0}%`} />
          </SimpleGridLike>
          {!activeRecommendation.targetFound && (
            <Text fontSize="xs" color="orange.600" mt={3}>
              We keep trying alternate keywords and tighter neighborhoods until this business appears in the top 20.
            </Text>
          )}
        </Box>
      )}

      <Button
        size="sm"
        variant="ghost"
        alignSelf="flex-start"
        leftIcon={<Icon as={showAlternates ? LuChevronUp : LuChevronDown} boxSize="14px" />}
        onClick={() => setShowAlternates((open) => !open)}
      >
        {showAlternates ? 'Hide alternate searches' : 'Show alternate searches'}
      </Button>

      <Collapse in={showAlternates} animateOpacity>
        <Box overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Search</Th>
                <Th isNumeric>Match quality</Th>
                <Th isNumeric>Score</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(resolution?.alternates || []).map((item) => (
                <Tr
                  key={item.query}
                  cursor="pointer"
                  bg={selectedAlternateQuery === item.query || (!selectedAlternateQuery && item.query === resolution?.recommended?.query) ? 'blue.50' : undefined}
                  _hover={{ bg: 'gray.50' }}
                  onClick={() => {
                    setSelectedAlternateQuery(item.query);
                    setEditedKeyword(item.query);
                  }}
                >
                  <Td>{item.query}</Td>
                  <Td isNumeric>
                    <Text as="span" color={`${qualityColor(item.qualityLabel)}.600`} fontWeight="600">
                      {item.qualityLabel}
                    </Text>
                  </Td>
                  <Td isNumeric>{item.score}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Collapse>

      {resolutionError && (
        <Text fontSize="sm" color="red.500">
          {resolutionError}
        </Text>
      )}

      <Divider borderColor={subtleBorder} />

      <HStack spacing={3}>
        <Button
          variant="ghost"
          leftIcon={<Icon as={LuArrowLeft} boxSize="14px" />}
          onClick={() => {
            setWizardStep(1);
            setResolution(null);
            setResolutionError('');
          }}
          borderRadius="10px"
        >
          Back
        </Button>
        <Button
          variant="outline"
          onClick={handleReResolve}
          isLoading={isResolving}
          borderRadius="10px"
        >
          Re-test keyword
        </Button>
        <Button
          colorScheme="blue"
          flex={1}
          leftIcon={<Icon as={LuCheck} boxSize="15px" />}
          onClick={handleUseRecommended}
          isLoading={isValidatingSelection}
          isDisabled={!displayKeyword.trim() || isResolving || !activeRecommendation?.targetFound}
          borderRadius="10px"
        >
          Use this market search
        </Button>
      </HStack>
    </VStack>
  );
}

function SimpleGridLike({ children, spacing = 4 }) {
  return (
    <Flex direction={{ base: 'column', md: 'row' }} gap={spacing}>
      {children}
    </Flex>
  );
}

function Metric({ label, value }) {
  const headingColor = useColorModeValue('gray.800', 'white');
  const subtleText = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box>
      <Text fontSize="10px" fontWeight="700" color={subtleText} textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="700" color={headingColor} mt={0.5}>
        {value}
      </Text>
    </Box>
  );
}

export default MarketSearchWizard;
